import { useEffect, useRef } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Page } from '@/shared/api/client';
import type { ApiError } from '@/shared/api/errors';
import { clientKeys } from '@/shared/clients/api';

import { proferSyncApi, proferSyncKeys } from './api';
import type { ClientSync, ClientSyncDetail } from './types';

/** While one runs, ask again every two seconds: the whole list takes a few. */
const RUNNING_POLL_MS = 2000;

/**
 * A sync still `running` after this long is a worker that stopped half way (a deploy, a
 * crash): the back closes it as `CLI-06` the next time somebody asks (`STALE_AFTER` in
 * `cubas.core.erp`). Until then the screen must not wait for it forever.
 */
export const STUCK_AFTER_MS = 30 * 60 * 1000;

/** Running and young enough to be really running. */
export function running(sync: ClientSync | undefined, now = Date.now()): boolean {
  return sync?.status === 'running' && !stuck(sync, now);
}

/** Still `running` long after any sync ends: nobody is going to finish it. */
export function stuck(sync: ClientSync | undefined, now = Date.now()): boolean {
  if (sync?.status !== 'running') return false;
  const started = Date.parse(sync.started_at);
  return Number.isFinite(started) && now - started > STUCK_AFTER_MS;
}

/**
 * The last syncs. Polled while the newest is running, and when it finishes the client
 * lists are refetched: what came in is on screen without reloading.
 */
export function useProferSyncs(enabled: boolean) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: proferSyncKeys.list,
    queryFn: () => proferSyncApi.list(),
    enabled,
    refetchInterval: (current) => (running(current.state.data?.items[0]) ? RUNNING_POLL_MS : false),
  });

  const latest = query.data?.items[0];
  const wasRunning = useRef<string | null>(null);
  useEffect(() => {
    if (!latest) return;
    if (running(latest)) {
      wasRunning.current = latest.id;
    } else if (wasRunning.current === latest.id) {
      wasRunning.current = null;
      void queryClient.invalidateQueries({ queryKey: clientKeys.lists });
      void queryClient.invalidateQueries({ queryKey: proferSyncKeys.detail(latest.id) });
    }
  }, [latest, queryClient]);

  return query;
}

export function useProferSync(id: string | null) {
  return useQuery<ClientSyncDetail, ApiError>({
    queryKey: proferSyncKeys.detail(id ?? ''),
    queryFn: () => proferSyncApi.detail(id ?? ''),
    enabled: Boolean(id),
    refetchInterval: (current) => (running(current.state.data) ? RUNNING_POLL_MS : false),
  });
}

/**
 * «Actualizar desde Profer». The sync it started goes at the top of the list at once, so
 * the line says «Actualizando…» from the answer on and the list notices when that one
 * ends, even when it ends before the list is read again (a quick Profer).
 */
export function useRequestProferSync() {
  const queryClient = useQueryClient();
  return useMutation<ClientSync, ApiError, void>({
    mutationFn: () => proferSyncApi.request(),
    onSuccess: (sync) => {
      queryClient.setQueryData<Page<ClientSync>>(proferSyncKeys.list, (page) =>
        page
          ? { ...page, items: [sync, ...page.items.filter((each) => each.id !== sync.id)] }
          : page,
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: proferSyncKeys.list });
    },
  });
}
